import {
  EvacZone,
  EvacZoneStatus,
  LayerEvacZone,
  RegionEvacZoneStyle,
} from 'shared/types';
import { EVAC_ZONE_STATUS_ENABLED } from '../../constants';
import { PendingZoneGroup } from './types';

// Gets every zone transitively linked to the starting zone through parent/child relationships
export const getZoneGroup = (
  startingZone: EvacZone,
  pendingZones: EvacZone[],
): EvacZone[] => {
  const pendingZonesByUid = new Map(
    pendingZones.map((zone) => [zone.uidV2, zone]),
  );
  const groupUids = new Set([startingZone.uidV2]);
  const zonesToVisit = [startingZone];

  while (zonesToVisit.length) {
    const zone = zonesToVisit.pop()!;
    [...(zone.childUids ?? []), ...(zone.parentUids ?? [])].forEach((uid) => {
      const linkedZone = pendingZonesByUid.get(uid);
      if (linkedZone && !groupUids.has(uid)) {
        groupUids.add(uid);
        zonesToVisit.push(linkedZone);
      }
    });
  }

  return pendingZones.filter((zone) => groupUids.has(zone.uidV2));
};

/**
 * Groups all pending zones into related operation groups not taking status into account
 * @param zones - All zones from the API
 * @returns Array of pending zone groups
 */
export const getPendingZoneGroups = (zones: EvacZone[]): PendingZoneGroup[] => {
  const pendingZones = zones.filter((zone) => zone.isPendingReview);
  const activeZones = pendingZones.filter((zone) => zone.isActive);

  const groups: PendingZoneGroup[] = [];
  const processedUids = new Set<string>();

  for (const evacZone of activeZones) {
    if (!processedUids.has(evacZone.uidV2)) {
      const zoneGroup = getZoneGroup(evacZone, pendingZones);
      const releaseUids = zoneGroup.map((zone) => zone.uidV2);
      groups.push({ zones: zoneGroup, releaseUids });

      // Mark all zones in this group as processed
      zoneGroup.forEach((zone) => processedUids.add(zone.uidV2));
    }
  }

  return groups;
};

const transformZoneForLayer = (
  zone: EvacZone,
  status?: EvacZoneStatus,
): LayerEvacZone => ({
  uidV2: zone.uidV2,
  style: zone.region.evacZoneStyle,
  status,
});

export type ZonesForMapLayers = {
  evacZones: LayerEvacZone[];
  pendingEvacZones: LayerEvacZone[];
};

/**
 * Processes evacuation zones and separates them into two categories:
 * regular zones and pending zones based on the provided releasePendingZoneUids.
 */
export const prepareZonesForMapLayers = (
  regionEvacZones: EvacZone[],
  evacZoneStatuses: Array<{
    evacZone: { id: number; uidV2: string };
    status: EvacZoneStatus;
  }>,
  releasePendingZoneUids?: string[],
): ZonesForMapLayers => {
  const evacZoneStatusMap = new Map(
    evacZoneStatuses.map((entry) => [entry.evacZone.uidV2, entry.status]),
  );
  const transformedZones = regionEvacZones.map((zone) => {
    return transformZoneForLayer(zone, evacZoneStatusMap.get(zone.uidV2));
  });

  // return early if no pending zones are being released
  if (!releasePendingZoneUids || releasePendingZoneUids.length === 0) {
    return {
      evacZones: transformedZones,
      pendingEvacZones: [],
    };
  }

  const isPendingApproved = (zone: LayerEvacZone): boolean => {
    return releasePendingZoneUids.includes(zone.uidV2);
  };

  const pendingNamesByUid = new Map<string, string>();
  regionEvacZones.forEach((zone) => {
    if (zone.pendingDisplayName) {
      pendingNamesByUid.set(zone.uidV2, zone.pendingDisplayName);
    }
  });

  const results: ZonesForMapLayers = { evacZones: [], pendingEvacZones: [] };
  transformedZones.forEach((zone) => {
    if (isPendingApproved(zone)) {
      // Accepted rename holds carry the pending name so map labels can preview it.
      const pendingDisplayName = pendingNamesByUid.get(zone.uidV2);
      results.pendingEvacZones.push(
        pendingDisplayName ? { ...zone, pendingDisplayName } : zone,
      );
    } else {
      results.evacZones.push(zone);
    }
  });
  return results;
};

export const getEnabledEvacZoneStatuses = (
  zoneStyles: RegionEvacZoneStyle[],
): EvacZoneStatus[] => {
  return [
    ...new Set(
      zoneStyles.flatMap((style) =>
        Object.entries(EVAC_ZONE_STATUS_ENABLED[style])
          .filter(([_, enabled]) => enabled === true)
          .map(([key]) => key as EvacZoneStatus),
      ),
    ),
  ];
};

export const zoneRequiresApproval = (
  zone: { uidV2: string; isPendingReview?: boolean; parentUids?: string[] },
  releasePendingZoneUids: string[],
): boolean => {
  // if zone is not pending review, no approval is needed
  if (!zone.isPendingReview) {
    return false;
  }

  // if no pending zones are being released, then approval is still needed
  if (releasePendingZoneUids.length === 0) {
    return true;
  }

  // check if the zone is in the list of pending zones being released
  if (releasePendingZoneUids.includes(zone.uidV2)) {
    return false;
  }

  // check if the zone has parent zones that are all released
  if (zone.parentUids && zone.parentUids.length > 0) {
    return !zone.parentUids.every((parentUid) =>
      releasePendingZoneUids.includes(parentUid),
    );
  }

  // otherwise, approval is still needed
  return true;
};

// Zones with accepted rename holds preview their pending name pre-submission.
export const getEffectiveEvacZones = (
  evacZones: EvacZone[],
  acceptedPendingZoneUids: string[] | undefined,
): {
  effectiveEvacZones: EvacZone[];
  effectiveZoneNamesByUid: Map<string, string>;
} => {
  const acceptedUids = new Set(acceptedPendingZoneUids ?? []);
  const effectiveEvacZones = evacZones.map((zone) =>
    acceptedUids.has(zone.uidV2) && zone.pendingDisplayName
      ? { ...zone, displayName: zone.pendingDisplayName }
      : zone,
  );
  return {
    effectiveEvacZones,
    effectiveZoneNamesByUid: new Map(
      effectiveEvacZones.map((zone) => [zone.uidV2, zone.displayName]),
    ),
  };
};
